import {
  fromExtracted,
  NumericSeries,
  PlotCollection,
  PlotSeriesDescriptor,
  regressSeries,
  type BarWidthValue,
  type NumericPoint,
  type OhlcValue,
  type PointValue,
  type RangeYValue,
  type RecordFieldRole,
  type RecordFieldRoles,
  type RecordValue,
  type RegressionParams,
} from "@tento-data";
import type { DataIndex, Option } from "@tento-lona/sheets";
import { type NumericExtent, unionYExtent } from "@tento-data-ui";

export type PlotFrame = Readonly<{
  xRange: DataIndex.DataFrame["xRange"];
  paddedRange: DataIndex.DataFrame["xRange"];
  yAxis: DataIndex.DataFrame["yAxis"];
  /**
   * Auto-fit y-extent shared across every series over the visible
   * x-range — the single scale the candles/lines map against and the
   * y-axis labels in the legend. `null` when no series yields data.
   */
  yExtent: Option<NumericExtent>;
  /**
   * X-axis tick values, in the same coordinate space as `xRange` —
   * already evaluated by the caller's `xTicks` closure against the
   * visible range. `null` when the preset declares no ticks; the canvas
   * then draws no x-axis furniture at all.
   */
  xTicks: Option<readonly number[]>;
  /**
   * Where "now" falls on this frame's x-axis, in the same coordinate
   * space as `xRange` — already evaluated by the caller's `xNow` closure
   * during this frame build, so the cursor advances exactly as fast as
   * the frame is rebuilt. `null` when the preset declares no cursor (a
   * frozen historical view) or when the domain does not cover now; the
   * canvas then draws nothing.
   */
  xNow: Option<number>;
  rendererSpecs: DataIndex.DataFrame["rendererSpecs"];
  collection: PlotCollection;
}>;

type FramePoint = DataIndex.DataFrameSeries["points"][number];
type FrameValue = FramePoint[1];
type ShapeKind = "scalar" | "bar-width" | "range-y" | "ohlc" | "point" | "record";

export function plotFrameFromDataFrame(
  frame: DataIndex.DataFrame,
  xRange: Option<DataIndex.DataFrame["xRange"]>,
  xTicks: Option<readonly number[]>,
  xNow: Option<number>,
): PlotFrame {
  const resolvedXRange = xRange ?? frame.xRange;
  const collection = plotCollectionFromDataFrame(frame);
  // GLOBAL y-extent: prefer the preset's configured `yAxis` (a fixed band that
  // does NOT re-fit as the x-axis pans); otherwise fall back to an auto-fit
  // over EVERY point (null range = all points), not the visible window — so
  // even unconfigured charts keep a stable scale while scrolling.
  const yExtent = frame.yAxis
    ? { min: frame.yAxis.min, max: frame.yAxis.max }
    : unionYExtent(collection.entries(), null) ?? null;
  return Object.freeze({
    xRange: resolvedXRange,
    paddedRange: frame.xRange,
    yAxis: frame.yAxis,
    yExtent,
    xTicks: xTicks != null && xTicks.length > 0 ? Object.freeze([...xTicks]) : null,
    // Only a finite position is a position: a NaN from a caller's arithmetic
    // would otherwise reach `toCanvasX` and stroke at NaN (silently nothing,
    // but on some paths a ruined path state).
    xNow: xNow != null && Number.isFinite(xNow) ? xNow : null,
    rendererSpecs: frame.rendererSpecs,
    collection,
  });
}

export function plotCollectionFromDataFrame(
  frame: DataIndex.DataFrame,
): PlotCollection {
  const entries: PlotCollection.AnyEntry[] = [];
  for (const series of frame.series.values()) {
    entries.push(entryFromSeries(series));
  }
  return PlotCollection.fromEntries(entries);
}

function entryFromSeries(
  series: DataIndex.DataFrameSeries,
): PlotCollection.AnyEntry {
  const shape = inferShape(series.points);
  switch (shape) {
    case "scalar": {
      const points = scalarPoints(series.points);
      const derived = series.type === "regression"
        ? regressSeries(NumericSeries.fromPoints(points), regressionParams(series))
        : null;
      return fromExtracted(
        PlotSeriesDescriptor.scalar(series.key, series.label, series.yRange, null),
        points,
        derived,
      );
    }
    case "bar-width":
      return fromExtracted(
        PlotSeriesDescriptor.barWidth(series.key, series.label, series.yRange, null),
        barWidthPoints(series.points),
      );
    case "range-y":
      return fromExtracted(
        PlotSeriesDescriptor.rangeY(series.key, series.label, series.yRange, null),
        rangeYPoints(series.points),
      );
    case "ohlc":
      return fromExtracted(
        PlotSeriesDescriptor.ohlc(series.key, series.label, series.yRange, null),
        ohlcPoints(series.points),
      );
    case "point":
      return fromExtracted(
        PlotSeriesDescriptor.point(series.key, series.label, series.yRange, null),
        pointPoints(series.points),
      );
    case "record": {
      const points = recordPoints(series.points);
      return fromExtracted(
        PlotSeriesDescriptor.record(
          series.key,
          recordRoles(points),
          series.label,
          series.yRange,
          null,
        ),
        points,
      );
    }
  }
}

function inferShape(points: readonly FramePoint[]): ShapeKind {
  for (const [, value] of points) {
    if (scalarValue(value) != null) return "scalar";
    if (barWidthValue(value) != null) return "bar-width";
    if (ohlcValue(value) != null) return "ohlc";
    if (rangeYValue(value) != null) return "range-y";
    if (pointValue(value) != null) return "point";
    if (recordValue(value) != null) return "record";
  }
  return "scalar";
}

function scalarPoints(points: readonly FramePoint[]): NumericPoint<number>[] {
  const out: NumericPoint<number>[] = [];
  for (const [x, value] of points) {
    const y = scalarValue(value);
    if (y != null) out.push([x, y]);
  }
  return out;
}

function barWidthPoints(points: readonly FramePoint[]): NumericPoint<BarWidthValue>[] {
  const out: NumericPoint<BarWidthValue>[] = [];
  for (const [x, value] of points) {
    const y = barWidthValue(value);
    if (y != null) out.push([x, y]);
  }
  return out;
}

function rangeYPoints(points: readonly FramePoint[]): NumericPoint<RangeYValue>[] {
  const out: NumericPoint<RangeYValue>[] = [];
  for (const [x, value] of points) {
    const y = rangeYValue(value);
    if (y != null) out.push([x, y]);
  }
  return out;
}

function ohlcPoints(points: readonly FramePoint[]): NumericPoint<OhlcValue>[] {
  const out: NumericPoint<OhlcValue>[] = [];
  for (const [x, value] of points) {
    const y = ohlcValue(value);
    if (y != null) out.push([x, y]);
  }
  return out;
}

function pointPoints(points: readonly FramePoint[]): NumericPoint<PointValue>[] {
  const out: NumericPoint<PointValue>[] = [];
  for (const [x, value] of points) {
    const y = pointValue(value);
    if (y != null) out.push([x, y]);
  }
  return out;
}

function recordPoints(points: readonly FramePoint[]): NumericPoint<RecordValue>[] {
  const out: NumericPoint<RecordValue>[] = [];
  for (const [x, value] of points) {
    const y = recordValue(value);
    if (y != null) out.push([x, y]);
  }
  return out;
}

function scalarValue(value: FrameValue): Option<number> {
  return finiteNumber(value);
}

function barWidthValue(value: FrameValue): Option<BarWidthValue> {
  if (Array.isArray(value)) {
    const y = finiteNumber(value[0]);
    const width = finiteNumber(value[1]);
    if (y != null && width != null) return Object.freeze({ y, width });
  }

  const object = recordValue(value);
  if (object == null) return null;
  const y = numberField(object, "y") ?? numberField(object, "value");
  const width = numberField(object, "width") ?? numberField(object, "w");
  if (y == null || width == null) return null;
  return Object.freeze({ y, width });
}

function rangeYValue(value: FrameValue): Option<RangeYValue> {
  const object = recordValue(value);
  if (object == null) return null;
  const low = numberField(object, "low") ?? numberField(object, "lo") ??
    numberField(object, "min");
  const high = numberField(object, "high") ?? numberField(object, "hi") ??
    numberField(object, "max");
  if (low == null || high == null) return null;
  return Object.freeze({ low, high });
}

function ohlcValue(value: FrameValue): Option<OhlcValue> {
  const object = recordValue(value);
  if (object == null) return null;
  const open = numberField(object, "open") ?? numberField(object, "o");
  const high = numberField(object, "high") ?? numberField(object, "h");
  const low = numberField(object, "low") ?? numberField(object, "l");
  const close = numberField(object, "close") ?? numberField(object, "c");
  if (open == null || high == null || low == null || close == null) return null;
  return Object.freeze({ open, high, low, close });
}

function pointValue(value: FrameValue): Option<PointValue> {
  const object = recordValue(value);
  if (object == null) return null;
  const y = numberField(object, "y");
  if (y == null) return null;
  const radius = numberField(object, "radius") ?? null;
  const color = typeof object["color"] === "string" ? object["color"] : null;
  return Object.freeze({ y, radius, color });
}

function recordValue(value: FrameValue): Option<RecordValue> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return Object.freeze({ ...value });
}

function recordRoles(points: readonly NumericPoint<RecordValue>[]): RecordFieldRoles {
  const roles: Record<string, RecordFieldRole> = {};
  for (const [, value] of points) {
    for (const key of Object.keys(value)) {
      roles[key] = roleForField(key);
    }
  }
  return Object.freeze(roles);
}

function roleForField(key: string): RecordFieldRole {
  switch (key) {
    case "y":
    case "value":
      return "y";
    case "radius":
    case "r":
      return "radius";
    case "color":
      return "color";
    case "open":
    case "o":
      return "open";
    case "high":
    case "h":
      return "high";
    case "low":
    case "l":
      return "low";
    case "close":
    case "c":
      return "close";
    default:
      return "custom";
  }
}

function regressionParams(series: DataIndex.DataFrameSeries): RegressionParams {
  return Object.freeze({
    convHalfWidth: series.convHalfWidth,
    maxGap: series.maxGap,
    minPoints: series.minPoints,
    minCoverage: series.minCoverage,
  });
}

function numberField(
  object: Readonly<Record<string, unknown>>,
  key: string,
): Option<number> {
  return finiteNumber(object[key]);
}

function finiteNumber(value: unknown): Option<number> {
  if (typeof value !== "number") return null;
  return Number.isFinite(value) ? value : null;
}
